import { Component, mixins } from 'vue-facing-decorator';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { clone } from 'ramda';
import { CalendarInstancesUserDatesParamsType, CalendarReservedSlotsType, CalendarInstancesUserDatesResponseType } from '@/types/Events/CalendarInstanceType';
import { getFirstDayOfMonth, getLastDayOfMonth, equalByDay, getDateAsIsoString, getDateWithDelayInDays } from '@/helpers/dates';
import { AppointmentWebApi } from '@/api/appointment/AppointmentApi';
import { BaseMetaType } from '@/types/Response';
import { EventType } from '@/types/Events/Base';
import { EventsWebApi } from '@/api/event/EventsApi';
import { calculateMonthCalendarViewDays } from '@/helpers/table';
import AccountMixin from '@/mixins/account';
import { useCalendarPageStore } from '@/stores/useCalendarPageStore';

@Component
export default class CalendarMixin extends mixins(AccountMixin) {
    ONE_DAY_MS = 1000 * 60 * 60 * 24;
    CALENDAR_ROUTES = {
        WEEK: '/calendar/main',
        MONTH: '/calendar/main/month',
    } as const;

    calendarPageStore = useCalendarPageStore();

    meta: BaseMetaType | null = null;
    isDeleteRequestSending: boolean = false;
    calendarMonth: number = new Date().getMonth() + 1;
    calendarYear: number = new Date().getFullYear();
    prevMonth: string = getDateAsIsoString(new Date(), true);

    get isWeekType(): boolean {
        return this.$route.path === this.CALENDAR_ROUTES.WEEK;
    }

    get isMonthType(): boolean {
        return this.$route.path === this.CALENDAR_ROUTES.MONTH;
    }

    getDateRangeForMonth(date: Date): { from: string; to: string } {
        const firstDayOfMonth: Date = getFirstDayOfMonth(date);
        const lastDayOfMonth: Date = getLastDayOfMonth(date, 1);
        return {
            from: format(firstDayOfMonth, this.baseFormat),
            to: format(lastDayOfMonth, this.baseFormat),
        };
    }

    refreshDots(date?: Date | null): void {
        const { from, to } = this.getDateRangeForMonth(date || this.date);
        this.getMothDates({ from, to, company_id: this.user.company_id }, true);
    }

    commitEventsToStore(events: Array<EventType>, forceUpdate: boolean): void {
        if (forceUpdate) {
            // @ts-ignore
            this.calendarPageStore.SET_CALENDAR_EVENTS(events);
        } else {
            // @ts-ignore
            this.calendarPageStore.CALENDAR_COMMIT_EVENTS_CONCAT(events);
        }
    }

    updateDataFromServer(serverData: any, forceUpdate: boolean = false, data: CalendarInstancesUserDatesParamsType | null = null): void {
        this.meta = serverData.meta;
        const events = serverData.calendar_instances;
        const preparedEvents = this.prepareSlots(events);
        this.commitEventsToStore(preparedEvents, forceUpdate);
    }

    prepareSlots<T extends Array<EventType>>(slots: T): T {
        const slotsCopy: T = clone(slots);
        const pseudoSlots: Array<EventType> = [];
        const ONE_DAY = 1000 * 60 * 60 * 24;
        for (let i = 0; i < slots.length; i++) {
            const startDate = new Date(slots[i].dt_start);
            const endDate = new Date(slots[i].dt_end);
            const startDayOnNight = new Date(startDate.getTime());
            const endDayOnNight = new Date(endDate.getTime());
            startDayOnNight.setHours(0, 0, 0, 0);
            endDayOnNight.setHours(0, 0, 0, 0);
            const diffTime = Math.abs(+endDayOnNight - +startDayOnNight);
            const diffDays = diffTime / ONE_DAY + 1;
            if (equalByDay(startDate, endDate)) {
                // is events start and end one one day
                continue;
            } else {
                // is events start and finish on next day
                const pseudoSlotFirstDay = clone(slots[i]);
                const pseudoSlotLastDay = clone(slots[i]);
                const dtStartDate = new Date(pseudoSlotFirstDay.dt_start);
                const dtEndDate = new Date(pseudoSlotLastDay.dt_end);
                pseudoSlotFirstDay.dt_start = format(dtStartDate, this.baseFormat);
                pseudoSlotFirstDay.dt_end = format(this.getDayDateNearNight(dtStartDate), this.baseFormat);
                pseudoSlotFirstDay.dt_start_view = pseudoSlotFirstDay.dt_start;
                pseudoSlotFirstDay.dt_end_view = pseudoSlotFirstDay.dt_end;
                pseudoSlotFirstDay.is_pseudo_slot = true;
                pseudoSlotFirstDay.finish_on_night = true;
                pseudoSlots.push(pseudoSlotFirstDay);

                const countOfFullDay = diffDays - 2;
                if (countOfFullDay >= 1) {
                    for (let j = 0; j < countOfFullDay; j++) {
                        const pseudoSlot = clone(slots[i]);
                        const thisDay = new Date(pseudoSlot.dt_start);
                        thisDay.setDate(new Date(pseudoSlot.dt_start).getDate() + 1 + j);
                        const localStartDate = new Date(thisDay.getTime());
                        const localEndDate = new Date(thisDay.getTime());
                        pseudoSlot.dt_start = format(this.getDayDateNearMorning(localStartDate), this.baseFormat);
                        pseudoSlot.dt_end = format(this.getDayDateNearNight(localEndDate), this.baseFormat);
                        pseudoSlot.dt_start_view = pseudoSlot.dt_start;
                        pseudoSlot.dt_end_view = pseudoSlot.dt_end;
                        pseudoSlot.is_pseudo_slot = true;
                        pseudoSlot.fully_day = true;
                        pseudoSlots.push(pseudoSlot);
                    }
                }

                pseudoSlotLastDay.dt_end = format(dtEndDate, this.baseFormat);
                pseudoSlotLastDay.dt_start = format(this.getDayDateNearMorning(dtEndDate), this.baseFormat);
                pseudoSlotLastDay.dt_start_view = pseudoSlotLastDay.dt_start;
                pseudoSlotLastDay.dt_end_view = pseudoSlotLastDay.dt_end;
                pseudoSlotLastDay.is_pseudo_slot = true;
                pseudoSlotLastDay.start_from_morning = true;
                pseudoSlots.push(pseudoSlotLastDay);
            }
            const currentIndex = slotsCopy.findIndex((item: EventType) => item.id === slots[i].id);
            slotsCopy.splice(currentIndex, 1);
        }
        // @ts-ignore-next-line
        return [...slotsCopy, ...pseudoSlots];
    }

    createReservedSlotsFromData(monthData: Array<CalendarInstancesUserDatesResponseType>): Array<CalendarReservedSlotsType> {
        const reservedSlots: Array<CalendarReservedSlotsType> = [];
        for (let i = 0; i < monthData.length; i++) {
            const userData: CalendarInstancesUserDatesResponseType = monthData[i];
            for (let j = 0; j < userData.dates.length; j++) {
                reservedSlots.push({
                    user_id: userData.user_id,
                    date: new Date(userData.dates[j]),
                });
            }
        }
        return reservedSlots;
    }

    commitReservedSlotsToStore(reservedSlots: Array<CalendarReservedSlotsType>, forceUpdate: boolean): void {
        this.calendarPageStore.SET_EVENT_FIRST_INITIAL_EXIST(!!reservedSlots.length);
        if (forceUpdate) {
            this.calendarPageStore.SET_CALENDAR_EVENTS_SLOTS(reservedSlots);
        } else {
            this.calendarPageStore.CALENDAR_COMMIT_RESERVED_SLOTS_CONCAT(reservedSlots);
        }
    }

    async getMothDates({ to, from, company_id }: CalendarInstancesUserDatesParamsType, forceUpdate: boolean = false) {
        const monthData: Array<CalendarInstancesUserDatesResponseType> = await AppointmentWebApi.getUserDates({ to, from, company_id });
        const reservedSlots = this.createReservedSlotsFromData(monthData);
        this.commitReservedSlotsToStore(reservedSlots, forceUpdate);
    }

    async getMonthDatesFirstInitial(firstInitialData: Array<CalendarInstancesUserDatesResponseType>) {
        const reservedSlots = this.createReservedSlotsFromData(firstInitialData);
        const forceUpdate = !!firstInitialData;
        this.commitReservedSlotsToStore(reservedSlots, forceUpdate);
    }

    async getCalendarData(date: Date = this.date, forceUpdate: boolean = false, onlyTimeSlots: boolean = false, user_id?: string | undefined) {
        if (this.isWeekType) {
            this.getCalendarDataWeek(date, forceUpdate, onlyTimeSlots, user_id);
        } else if (this.isMonthType) {
            this.getCalendarDataMonth(date, forceUpdate);
        }
    }

    checkAndUpdateReservedSlots(month: string, from: string, to: string, forceUpdate: boolean = false): void {
        if (!this.calendarPageStore.events_reserved_slots_exists.includes(month) || forceUpdate) {
            if (this.prevMonth !== month) {
                this.getMothDates({ to, from, company_id: this.user.company_id }, forceUpdate);
            }
            if (forceUpdate) {
                this.calendarPageStore.REPLACE_CALENDAR_RESERVED_SLOTS_EXISTS(month);
            } else {
                this.calendarPageStore.CALENDAR_RESERVED_SLOTS_EXISTS(month);
            }
        }
    }

    async getCalendarDataMonth(date: Date, forceUpdate: boolean = false) {
        this.datesLoading = true;
        const dates: Array<{ year: number, day: number, month: number}> = calculateMonthCalendarViewDays(this.calendarYear, this.calendarMonth - 1, this.firstDayOfWeek);
        const firstDay = dates[0];
        const firstDayOfMonth: Date = new Date(firstDay.year, firstDay.month, firstDay.day);
        const lastDay = dates[dates.length - 1];
        const lastDayOfMonth: Date = new Date(lastDay.year, lastDay.month, lastDay.day);
        await this.fetchCalendarDataRec(format(firstDayOfMonth, this.baseFormat), format(lastDayOfMonth, this.baseFormat), 1, forceUpdate);
        this.datesLoading = false;
        const month = getDateAsIsoString(firstDayOfMonth, true);
        const from = format(firstDayOfMonth, this.baseFormat);
        const to = format(lastDayOfMonth, this.baseFormat);

        this.checkAndUpdateReservedSlots(month, from, to, forceUpdate);
        this.prevMonth = month;
    }

    getWeekDateRange(date: Date): { weekStart: string; weekEnd: string } {
        const copyDate = (localData: Date) => new Date(localData.getTime());
        const dt_week_start_date: Date = dayjs(copyDate(date)).startOf('day').toDate();
        const dt_week_end_date: Date = getDateWithDelayInDays(copyDate(date), 7);
        return {
            weekStart: format(dt_week_start_date, this.baseFormat),
            weekEnd: format(dt_week_end_date, this.baseFormat),
        };
    }

    async getCalendarDataWeek(date: Date, forceUpdate: boolean = false, onlyTimeSlots: boolean = false, user_id?: string | undefined) {
        if (!onlyTimeSlots) {
            this.datesLoading = true;
        }

        const { from, to } = this.getDateRangeForMonth(date);
        const { weekStart, weekEnd } = this.getWeekDateRange(date);
        const month = getDateAsIsoString(getFirstDayOfMonth(date), true);

        this.checkAndUpdateReservedSlots(month, from, to, forceUpdate);
        this.prevMonth = month;

        if (!onlyTimeSlots) {
            await this.fetchCalendarDataRec(weekStart, weekEnd, 1, forceUpdate, user_id);
            this.datesLoading = false;
        }
    }

    async updateAllData(date: Date | null) {
        this.refreshDots(date);
        await this.getCalendarData(dayjs(date || this.date).startOf('day').toDate(), true);
        await this.$nextTick();
        this.updateKey += 1;
    }

    async removeEvent(
        { instance, onlyInstance, future }: { instance: EventType, onlyInstance?: boolean, future?: boolean }
    ) {
        this.isDeleteRequestSending = true;
        try {
            if (onlyInstance || future) {
                await EventsWebApi.deleteCalendarInstance(instance.instance_id!, Boolean(future));
            } else {
                await EventsWebApi.deleteEvent(instance.id);
            }
            this.sentNotif('NOTIFICATION.BAR.SUCCESS.BAR.REMOVED', true);
        } catch ({ response }) {
            this.sentNotif(response);
        } finally {
            this.refreshDots();
            this.isDeleteRequestSending = false;
        }
        this.showAddPopup = false;
        this.showDeletePopup = false;
        this.getCalendarData(this.date, true);
    }

    async handleApiCall<T>(apiCall: () => Promise<T>): Promise<T | null> {
        try {
            return await apiCall();
        } catch ({ response }) {
            this.sentNotif(response);
            return null;
        }
    }

    async fetchCalendarDataByPage(from: string, to: string, page: number = 1, forceUpdate: boolean = false, user_id?: string | undefined) {
        const response = await this.handleApiCall(() =>
            AppointmentWebApi.getInstancesData({ from, to, page, user_id })
        );

        if (response) {
            this.updateDataFromServer(response.data, forceUpdate);
        }
    }

    async fetchCalendarDataRec(from: string, to: string, page: number = 1, forceUpdate: boolean = false, user_id?: string | undefined) {
        await this.fetchCalendarDataByPage(from, to, page, forceUpdate, user_id);
        if (this.meta && this.meta.next_page && !this.isImaSanteAccount && !this.isImaMedicalAccount) {
            await this.fetchCalendarDataRec(from, to, this.meta.next_page);
        }
    }

    changeCalendarMonth(data: { year: number, month: number, flag: number, vm: any, sibling: undefined }) {
        this.calendarMonth = data.month + data.flag + 1;
        this.calendarYear = data.year;
        this.getCalendarData(new Date(data.year, data.month + data.flag + 1, 0), false, true);
    }
};
